import { Box, VStack, HStack, Text, Button, Badge, IconButton, Tabs, TabList, TabPanels, Tab, TabPanel, Avatar, useColorModeValue, useBreakpointValue, useToast, Spinner, Center, Progress } from '@chakra-ui/react';
import { FiArrowLeft, FiThumbsUp, FiThumbsDown, FiShare, FiHeart, FiBook, FiCalendar, FiGlobe, FiStar } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import BookReader from '../components/BookReader';
import CommentSection from '../components/CommentSection';
import { useAuth } from '../contexts/AuthContext';
import { addBookToLibrary, removeBookFromLibrary, isBookInLibrary } from '../services/bookLibraryService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { deleteContent } from '../services/contentDeletionService';
import EditBookModal from '../components/EditBookModal';

interface BookData {
  id: string;
  title: string;
  author: string;
  description: string;
  thumbnail: string;
  rating: number;
  year: number;
  genres: string[];
  language: string;
  uploader: string;
  uploaderId: string;
  uploaderAvatar?: string;
  views: number;
  likes: number;
  dislikes: number;
  tags: string[];
  category: string;
  assets?: {
    bookFile?: string;
    coverFile?: string;
  };
  createdAt: any;
  status: string;
  isPublic: boolean;
}

const BookDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [book, setBook] = useState<BookData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const { user } = useAuth();
  const toast = useToast();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const textColor = useColorModeValue('gray.900', 'white');
  const bgColor = useColorModeValue('white', 'gray.900');
  const descriptionColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const starEmptyColor = useColorModeValue('gray.300', 'whiteAlpha.400');
  
  // Responsive icon sizes
  const starIconSize = useBreakpointValue({ base: 14, md: 16 });
  const infoIconSize = useBreakpointValue({ base: 14, md: 16 });
  const buttonIconSize = useBreakpointValue({ base: 16, md: 20 });
  const interactionIconSize = useBreakpointValue({ base: 18, md: 20 });

  // Fetch book data from Firebase
  useEffect(() => {
    const fetchBookData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const bookDoc = await getDoc(doc(db, 'content', id));
        
        if (bookDoc.exists()) {
          const bookData = { id: bookDoc.id, ...bookDoc.data() } as BookData;
          
          // Check if it's actually a book
          if (bookData.category !== 'book') {
            setError('Content not found');
            return;
          }
          
          setBook(bookData);
        } else {
          setError('Book not found');
        }
      } catch (error) {
        console.error('Error fetching book data:', error);
        setError('Failed to load book data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookData();
  }, [id]);

  // Listen for page focus to refresh data (useful after editing)
  useEffect(() => {
    const handleFocus = () => {
      if (id && book) {
        const fetchBookData = async () => {
          try {
            const bookDoc = await getDoc(doc(db, 'content', id));
            if (bookDoc.exists()) {
              const bookData = { id: bookDoc.id, ...bookDoc.data() } as BookData;
              setBook(bookData);
            }
          } catch (error) {
            console.error('Error refreshing book data:', error);
          }
        };
        fetchBookData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [id, book]);

  // Check if book is in library on component mount
  useEffect(() => {
    const checkLibraryStatus = async () => {
      if (user && id) {
        try {
          const inLibrary = await isBookInLibrary(user.uid, id);
          setIsInLibrary(inLibrary);
        } catch (error) {
          console.error('Error checking library status:', error);
        }
      }
    };
    checkLibraryStatus();
  }, [user, id]);

  const handleLibraryToggle = async () => {
    if (!user || !book) {
      toast({
        title: 'Please sign in',
        description: 'You need to be signed in to add books to your library',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoadingLibrary(true);
    try {
      if (isInLibrary) {
        await removeBookFromLibrary(user.uid, book.id);
        setIsInLibrary(false);
        toast({
          title: 'Removed from Library',
          description: `${book.title} has been removed from your library`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await addBookToLibrary(user.uid, book.id, {
          title: book.title,
          cover: book.thumbnail || book.assets?.coverFile || '',
          author: book.author
        });
        setIsInLibrary(true);
        toast({
          title: 'Added to Library',
          description: `${book.title} has been added to your library`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error toggling library status:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Center h="100vh">
        <VStack gap={4}>
          <Spinner size="xl" color="purple.500" />
          <Text color="white">Loading book...</Text>
        </VStack>
      </Center>
    );
  }

  // Show error state
  if (error || !book) {
    return (
      <Center h="100vh">
        <VStack gap={4}>
          <Text fontSize="xl" color="white">Book not found</Text>
          <Text color="gray.400">{error || 'The book you\'re looking for doesn\'t exist.'}</Text>
          <Text color="gray.400" fontSize="sm">Debug: error={error}, book={book ? 'exists' : 'null'}</Text>
          <Button colorScheme="purple" onClick={() => navigate('/books')}>
            Back to Books
          </Button>
        </VStack>
      </Center>
    );
  }

  const handleLike = () => {
    if (isDisliked) {
      setIsDisliked(false);
    }
    setIsLiked(!isLiked);
  };

  const handleDislike = () => {
    if (isLiked) {
      setIsLiked(false);
    }
    setIsDisliked(!isDisliked);
  };

  const handleWatchlist = () => {
    setIsInWatchlist(!isInWatchlist);
  };

  const handleDonate = () => {
    if (book?.uploaderId) {
      navigate(`/donation/${book.uploaderId}/${book.id}`, {
        state: { creatorName: book.uploader }
      });
    }
  };

  // Helper function to get book file URL from assets
  const getBookFileUrl = (assets: any) => {
    if (!assets) return '';
    
    // First try the bookFile property
    if (assets.bookFile) return assets.bookFile;
    
    // If bookFile doesn't exist, look for the first PDF/EPUB/MOBI file in assets
    const assetKeys = Object.keys(assets);
    const bookFileKey = assetKeys.find(key => {
      const lowerKey = key.toLowerCase();
      return lowerKey.endsWith('.pdf') || lowerKey.endsWith('.epub') || lowerKey.endsWith('.mobi');
    });
    
    if (bookFileKey) {
      return assets[bookFileKey];
    }
    
    return '';
  };

  const handleReadNow = () => {
    if (!book) return;
    
    const bookFileUrl = getBookFileUrl(book.assets);
    
    if (!bookFileUrl) {
      toast({
        title: 'Book not available',
        description: 'This book doesn\'t have a downloadable file yet.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Open book in new tab instead of modal
    window.open(bookFileUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteBook = async () => {
    if (!user || !book) return;
    
    if (user.uid !== book.uploaderId) {
      toast({
        title: 'Permission Denied',
        description: 'You can only delete your own books.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${book.title}"? This action cannot be undone and will remove all associated data including comments, likes, and files.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    setDeleteProgress(0);

    toast({
      title: 'Deleting Book...',
      description: 'Removing book and all associated data. This may take a moment.',
      status: 'info',
      duration: 10000,
      isClosable: true,
    });

    try {
      const progressInterval = setInterval(() => {
        setDeleteProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      const result = await deleteContent(book.id, user.uid);
      
      clearInterval(progressInterval);
      setDeleteProgress(100);
      
      if (result.success) {
        toast({
          title: 'Book Deleted Successfully',
          description: `Deleted ${result.deletedItems.files} files and all associated data.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setTimeout(() => navigate('/creator-dashboard'), 1000);
      } else {
        toast({
          title: 'Delete Failed',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Error deleting book:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
    }
  };


  return (
    <Box bg="white" _dark={{ bg: "gray.900" }} minH="100vh" className="page-content">
      {/* Header */}
      <Box bg="white" _dark={{ bg: "gray.900" }} py={{ base: 6, md: 8 }} borderBottom="1px solid" borderColor={borderColor}>
        <VStack gap={{ base: 4, md: 6 }} align="stretch" maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          {/* Back Button */}
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            colorScheme="gray"
            alignSelf="flex-start"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          />

          {/* Book Info */}
          <HStack gap={8} align="start" direction={{ base: 'column', md: 'row' }}>
            {/* Book Cover */}
            <Box flexShrink={0} w={{ base: '100%', md: 'auto' }} display="flex" justifyContent={{ base: 'center', md: 'flex-start' }}>
              <Box
                w={{ base: '180px', sm: '200px', md: '250px' }}
                h={{ base: '250px', sm: '280px', md: '350px' }}
                borderRadius="lg"
                overflow="hidden"
                boxShadow="2xl"
                border="3px solid"
                borderColor={borderColor}
              >
                <CachedImage
                  src={book.assets?.coverFile || book.thumbnail || ''}
                  alt={book.title}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  fallbackType="book"
                />
              </Box>
            </Box>

            {/* Book Details */}
            <VStack gap={{ base: 3, md: 4 }} align="start" flex="1" w="100%">
              <VStack gap={{ base: 1, md: 2 }} align="start" w="100%">
                <Text 
                  fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }} 
                  fontWeight="bold" 
                  color="gray.900"
                  _dark={{ color: "white" }}
                  lineHeight="shorter"
                  textAlign={{ base: 'center', md: 'left' }}
                  w="100%"
                >
                  {book.title}
                </Text>
                <Text 
                  fontSize={{ base: 'lg', md: 'xl' }} 
                  color="gray.600"
                  _dark={{ color: "gray.300" }}
                  textAlign={{ base: 'center', md: 'left' }}
                  w="100%"
                >
                  by {book.author}
                </Text>
              </VStack>

              {/* Rating */}
              <HStack gap={2} justify={{ base: 'center', md: 'flex-start' }} w="100%">
                <HStack gap={1}>
                  {[...Array(5)].map((_, i) => (
                    <IconWrapper
                      key={i}
                      icon={FiStar}
                      size={starIconSize}
                      color={i < Math.floor(book.rating) ? '#FFD700' : starEmptyColor}
                    />
                  ))}
                </HStack>
                <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize={{ base: 'xs', md: 'sm' }}>
                  {book.rating}/5.0
                </Text>
              </HStack>

              {/* Book Info */}
              <HStack gap={{ base: 4, md: 6 }} wrap="wrap" justify={{ base: 'center', md: 'flex-start' }} w="100%">
                <HStack gap={2}>
                  <IconWrapper icon={FiCalendar} size={infoIconSize} color={iconColor} />
                  <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize={{ base: 'xs', md: 'sm' }}>{book.year}</Text>
                </HStack>
                <HStack gap={2}>
                  <IconWrapper icon={FiBook} size={infoIconSize} color={iconColor} />
                  <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize={{ base: 'xs', md: 'sm' }}>{book.views} views</Text>
                </HStack>
                <HStack gap={2}>
                  <IconWrapper icon={FiGlobe} size={infoIconSize} color={iconColor} />
                  <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize={{ base: 'xs', md: 'sm' }}>{book.language}</Text>
                </HStack>
              </HStack>

              {/* Genres */}
              <HStack gap={2} wrap="wrap" justify={{ base: 'center', md: 'flex-start' }} w="100%">
                {book.genres.map((genre, index) => (
                  <Badge 
                    key={index} 
                    colorScheme="purple" 
                    variant="solid" 
                    px={{ base: 2, md: 3 }} 
                    py={{ base: 0.5, md: 1 }}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    {genre}
                  </Badge>
                ))}
              </HStack>

              {/* Action Buttons */}
              <VStack gap={3} w="100%" align={{ base: 'stretch', md: 'flex-start' }}>
                <HStack gap={3} w="100%" justify={{ base: 'center', md: 'flex-start' }}>
                  <Button
                    colorScheme="purple"
                    size={{ base: 'md', md: 'lg' }}
                    leftIcon={<IconWrapper icon={FiBook} size={buttonIconSize} />}
                    onClick={handleReadNow}
                    px={{ base: 6, md: 8 }}
                    py={{ base: 4, md: 6 }}
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="bold"
                    flex={{ base: '1', md: '0' }}
                    minW={{ base: '120px', md: 'auto' }}
                  >
                    Read Now
                  </Button>

                  <Button
                    colorScheme="white"
                    variant="outline"
                    size={{ base: 'md', md: 'lg' }}
                    onClick={handleLibraryToggle}
                    px={{ base: 4, md: 6 }}
                    isLoading={isLoadingLibrary}
                    loadingText={isInLibrary ? 'Removing...' : 'Adding...'}
                    flex={{ base: '1', md: '0' }}
                    minW={{ base: '120px', md: 'auto' }}
                  >
                    {isInLibrary ? 'In Library' : 'Add to Library'}
                  </Button>
                </HStack>

                {/* Book Owner Actions */}
                {user && book && user.uid === book.uploaderId && (
                  <VStack gap={3} w="100%" mt={2}>
                    <Text fontSize="sm" color="gray.600" _dark={{ color: "whiteAlpha.600" }} fontWeight="medium" textAlign={{ base: 'center', md: 'left' }}>
                      Book Management
                    </Text>
                    <HStack gap={3} w="100%">
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        size={{ base: 'sm', md: 'md' }}
                        flex="1"
                        onClick={() => navigate(`/book/${book.id}/edit`)}
                      >
                        Edit Book
                      </Button>
                      <Button
                        variant="outline"
                        colorScheme="red"
                        size={{ base: 'sm', md: 'md' }}
                        flex="1"
                        onClick={handleDeleteBook}
                        isLoading={isDeleting}
                        loadingText="Deleting..."
                        isDisabled={isDeleting}
                      >
                        Delete Forever
                      </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" _dark={{ color: "whiteAlpha.500" }} textAlign="center">
                      Delete: Permanently remove all data and files
                    </Text>
                  {isDeleting && (
                    <VStack gap={2} w="100%" mt={3}>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        Deleting book and files... {Math.round(deleteProgress)}%
                      </Text>
                      <Progress 
                        value={deleteProgress} 
                        size="sm" 
                        colorScheme="red" 
                        w="100%" 
                        borderRadius="full"
                      />
                    </VStack>
                  )}
                  </VStack>
                )}
              </VStack>
            </VStack>
          </HStack>
        </VStack>
      </Box>

      {/* Main Content */}
      <VStack gap={{ base: 6, md: 8 }} align="stretch" p={{ base: 4, md: 8 }} pb={{ base: 20, md: 8 }} maxW="1200px" mx="auto" bg="white" _dark={{ bg: "gray.900" }}>
        {/* Interaction Buttons */}
        <HStack justify="center" gap={{ base: 6, md: 8 }} py={4} wrap="wrap">
          <VStack gap={1}>
            <IconButton
              aria-label="Like"
              icon={<IconWrapper icon={FiThumbsUp} size={interactionIconSize} />}
              variant={isLiked ? 'solid' : 'outline'}
              colorScheme={isLiked ? 'green' : 'purple'}
              onClick={handleLike}
              size={{ base: 'md', md: 'lg' }}
            />
            <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor} fontWeight="medium">
              Like
            </Text>
          </VStack>
          
          <VStack gap={1}>
            <IconButton
              aria-label="Dislike"
              icon={<IconWrapper icon={FiThumbsDown} size={interactionIconSize} />}
              variant={isDisliked ? 'solid' : 'outline'}
              colorScheme={isDisliked ? 'red' : 'purple'}
              onClick={handleDislike}
              size={{ base: 'md', md: 'lg' }}
            />
            <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor} fontWeight="medium">
              Dislike
            </Text>
          </VStack>
          
          <VStack gap={1}>
            <IconButton
              aria-label="Share"
              icon={<IconWrapper icon={FiShare} size={interactionIconSize} />}
              variant="outline"
              colorScheme="purple"
              size={{ base: 'md', md: 'lg' }}
            />
            <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor} fontWeight="medium">
              Share
            </Text>
          </VStack>
          
          <VStack gap={1}>
            <IconButton
              aria-label={isInWatchlist ? "Remove from Library" : "Add to Library"}
              icon={<IconWrapper icon={FiHeart} size={interactionIconSize} />}
              variant={isInWatchlist ? 'solid' : 'outline'}
              colorScheme={isInWatchlist ? 'red' : 'purple'}
              onClick={handleWatchlist}
              size={{ base: 'md', md: 'lg' }}
            />
            <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor} fontWeight="medium">
              Library
            </Text>
          </VStack>
        </HStack>

        {/* Tabs */}
        <Tabs colorScheme="purple" index={activeTab} onChange={setActiveTab}>
          <TabList overflowX="auto" sx={{ '&::-webkit-scrollbar': { display: 'none' } }} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg" p={1}>
            <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 4 }} color="gray.600" _dark={{ color: "gray.300" }} _selected={{ color: "purple.600", _dark: { color: "purple.400" } }}>About</Tab>
            <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 4 }} color="gray.600" _dark={{ color: "gray.300" }} _selected={{ color: "purple.600", _dark: { color: "purple.400" } }}>Publisher</Tab>
            <Tab fontSize={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 4 }} color="gray.600" _dark={{ color: "gray.300" }} _selected={{ color: "purple.600", _dark: { color: "purple.400" } }}>Comments</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel px={0}>
              <VStack gap={{ base: 4, md: 6 }} align="start">
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
                  About This Book
                </Text>
                <Text color={descriptionColor} lineHeight="1.8" fontSize={{ base: 'md', md: 'lg' }}>
                  {book.description}
                </Text>
                
                <Box p={{ base: 4, md: 6 }} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg" w="100%" border="1px solid" borderColor={borderColor}>
                  <VStack gap={{ base: 3, md: 4 }} align="start">
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor}>Book Details</Text>
                    <HStack gap={{ base: 4, md: 8 }} wrap="wrap" justify={{ base: 'space-between', md: 'flex-start' }}>
                      <VStack align="start" gap={1} minW={{ base: '80px', md: 'auto' }}>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color={descriptionColor}>Views</Text>
                        <Text fontWeight="bold" color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{book.views}</Text>
                      </VStack>
                      <VStack align="start" gap={1} minW={{ base: '80px', md: 'auto' }}>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color={descriptionColor}>Language</Text>
                        <Text fontWeight="bold" color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{book.language}</Text>
                      </VStack>
                      <VStack align="start" gap={1} minW={{ base: '80px', md: 'auto' }}>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color={descriptionColor}>Published</Text>
                        <Text fontWeight="bold" color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{book.year}</Text>
                      </VStack>
                      <VStack align="start" gap={1} minW={{ base: '80px', md: 'auto' }}>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color={descriptionColor}>Rating</Text>
                        <Text fontWeight="bold" color={textColor} fontSize={{ base: 'sm', md: 'md' }}>{book.rating}/5.0</Text>
                      </VStack>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </TabPanel>
            
            <TabPanel px={0}>
              <VStack gap={{ base: 4, md: 6 }} align="start">
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
                  Publisher Information
                </Text>
                
                <Box p={{ base: 4, md: 6 }} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg" w="100%" border="1px solid" borderColor={borderColor}>
                  <HStack gap={{ base: 4, md: 6 }} direction={{ base: 'column', sm: 'row' }} align="flex-start">
                    <Avatar
                      size={{ base: 'lg', md: 'xl' }}
                      name={book.uploader}
                      src={book.uploaderAvatar}
                      bg="purple.500"
                    />
                    <VStack align="flex-start" gap={2} flex="1">
                      <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textColor}>
                        {book.uploader}
                      </Text>
                      <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }}>
                        Author & Publisher
                      </Text>
                      <HStack gap={3} pt={2} wrap="wrap" justify="flex-start">
                        <Button
                          colorScheme="purple"
                          variant="outline"
                          size={{ base: 'xs', md: 'sm' }}
                          onClick={() => navigate(`/profile/${book.uploaderId}`)}
                        >
                          View Profile
                        </Button>
                        <Button
                          colorScheme="red"
                          variant="outline"
                          size={{ base: 'xs', md: 'sm' }}
                          leftIcon={<IconWrapper icon={FiHeart} size={16} />}
                          onClick={handleDonate}
                        >
                          Support Author
                        </Button>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
              </VStack>
            </TabPanel>
            
            <TabPanel px={0}>
              <VStack gap={{ base: 4, md: 6 }} align="start">
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
                  Comments
                </Text>
                
                <Box p={{ base: 4, md: 6 }} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg" w="100%" border="1px solid" borderColor={borderColor}>
                  <VStack gap={{ base: 3, md: 4 }} align="start">
                    <Text color={descriptionColor} fontSize={{ base: 'sm', md: 'md' }}>
                      No comments yet. Be the first to comment on this book!
                    </Text>
                    <Button colorScheme="purple" size={{ base: 'xs', md: 'sm' }}>
                      Add Comment
                    </Button>
                  </VStack>
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Comments Section */}
      <Box maxW="1200px" mx="auto" px={isMobile ? 4 : 8} py={8} pb={{ base: 20, md: 8 }} bg="white" _dark={{ bg: "gray.900" }}>
        <CommentSection contentId={book.id} />
      </Box>


      {/* Edit Book Modal */}
      {book && user && (
        <EditBookModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          contentId={book.id}
          userId={user.uid}
          onSuccess={() => {
            // Refresh book data
            window.location.reload();
          }}
        />
      )}

      {/* Book Reader */}
      {isReaderOpen && book && (
        <BookReader
          bookId={book.id}
          bookTitle={book.title}
          bookFileUrl={getBookFileUrl(book.assets)}
          onClose={() => setIsReaderOpen(false)}
        />
      )}
    </Box>
  );
};

export default BookDetail;